import { useState } from 'react';
import { RefreshCw, Copy, Check, Loader2, Trash2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import GlowCard from '@/components/GlowCard';
import { cn } from '@/lib/utils';
import { useProfile } from '@/hooks/useProfile';
import { useToolUsage } from '@/hooks/useToolUsage';
import { toast } from 'sonner';
import { callTAMAI } from '@/lib/tamAI';

const modes = [
  { id: 'standard', label: 'Standard', icon: '✨', description: 'Basic paraphrasing' },
  { id: 'fluent', label: 'Fluent', icon: '💬', description: 'Natural flow' },
  { id: 'formal', label: 'Formal', icon: '📝', description: 'Professional tone' },
  { id: 'creative', label: 'Creative', icon: '🎨', description: 'Varied vocabulary' },
  { id: 'shorten', label: 'Shorten', icon: '📐', description: 'Condense text' },
];

const modeInstructions: Record<string, string> = {
  standard: 'Paraphrase this text using different words while keeping the same meaning.',
  fluent: 'Paraphrase this text to make it flow more naturally and conversationally.',
  formal: 'Paraphrase this text in a formal, professional, academic tone.',
  creative: 'Paraphrase this text using creative and varied vocabulary.',
  shorten: 'Paraphrase and condense this text to be shorter while keeping all key points.',
};

const FREE_CHAR_LIMIT = 5000;
const PRO_CHAR_LIMIT = 20000;

const Paraphraser = () => {
  const { isPremium } = useProfile();
  useToolUsage('paraphraser'); // Track tool usage

  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [mode, setMode] = useState('standard');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const charLimit = isPremium ? PRO_CHAR_LIMIT : FREE_CHAR_LIMIT;
  const wordCount = inputText.trim().split(/\s+/).filter(Boolean).length;
  const outputWordCount = outputText.trim().split(/\s+/).filter(Boolean).length;

  const handleParaphrase = async () => {
    if (!inputText.trim()) {
      toast.error('Please enter some text to paraphrase');
      return;
    }

    if (inputText.length > charLimit) {
      toast.error(`Text exceeds ${charLimit.toLocaleString()} character limit`);
      return;
    }

    setIsLoading(true);
    setOutputText('');
    setError(null);

    const prompt = `${modeInstructions[mode]}

TEXT TO PARAPHRASE:
"${inputText}"

IMPORTANT: Provide ONLY the paraphrased text. No introductions, no explanations, no quotes around the text. Just the rewritten content.`;

    try {
      const result = await callTAMAI(prompt, { temperature: 0.8 });

      // Clean up the result (remove quotes if present)
      const cleanedResult = result.replace(/^["']|["']$/g, '').trim();
      setOutputText(cleanedResult);
      toast.success('Text paraphrased successfully!');
    } catch (err) {
      console.error('Paraphrase error:', err);
      setError(err instanceof Error ? err.message : 'Failed to paraphrase. Please try again.');
      toast.error('Paraphrasing failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!outputText) return;
    await navigator.clipboard.writeText(outputText);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClear = () => {
    setInputText('');
    setOutputText('');
    setError(null);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-primary/10">
            <RefreshCw className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">AI Paraphraser</h1>
            <p className="text-muted-foreground">Rewrite text in different words • Powered by TAM API</p>
          </div>
        </div>
        <Badge variant="outline" className="text-xs">
          {isPremium ? 'Pro: 20K chars' : 'Free: 5K chars'}
        </Badge>
      </div>

      {/* Mode Selector */}
      <div className="flex flex-wrap gap-2">
        {modes.map((m) => (
          <Button
            key={m.id}
            variant={mode === m.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode(m.id)}
            className={cn(
              "hover-bounce transition-all",
              mode === m.id && "ring-2 ring-primary/50"
            )}
          >
            <span className="mr-1.5">{m.icon}</span>
            {m.label}
          </Button>
        ))}
      </div>
      <p className="text-sm text-muted-foreground">
        {modes.find(m => m.id === mode)?.description}
      </p>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input */}
        <GlowCard className="card-lift">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Original Text</h2>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {wordCount} words
              </Badge>
              <Badge variant={inputText.length > charLimit ? 'destructive' : 'outline'} className="text-xs">
                {inputText.length}/{charLimit.toLocaleString()}
              </Badge>
            </div>
          </div>

          <Textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Enter or paste your text here..."
            className="min-h-[250px] resize-none bg-muted/30"
          />

          <Progress
            value={(inputText.length / charLimit) * 100}
            className="mt-2 h-1"
          />

          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleParaphrase}
              disabled={isLoading || !inputText.trim()}
              className="flex-1 hover-bounce ripple"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Rewriting...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Paraphrase Text
                </>
              )}
            </Button>
            <Button variant="outline" onClick={handleClear} disabled={isLoading}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </GlowCard>

        {/* Output */}
        <GlowCard className="card-lift">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Paraphrased Text
            </h2>
            {outputText && (
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {outputWordCount} words
                </Badge>
                <Button variant="outline" size="sm" onClick={handleCopy}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="relative">
                <RefreshCw className="h-12 w-12 text-primary animate-spin" />
                <div className="absolute inset-0 rounded-full border-4 border-primary/20 animate-ping" />
              </div>
              <p className="mt-4 text-muted-foreground">Rewriting your text...</p>
              <div className="mt-2 flex gap-1">
                <span className="h-2 w-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0s' }} />
                <span className="h-2 w-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0.1s' }} />
                <span className="h-2 w-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0.2s' }} />
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/30 w-full">
                <p className="text-destructive">{error}</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={handleParaphrase}>
                  Try Again
                </Button>
              </div>
            </div>
          ) : outputText ? (
            <div className="animate-fade-in">
              <div className="p-4 rounded-lg bg-muted/30 min-h-[250px]">
                <p className="whitespace-pre-wrap">{outputText}</p>
              </div>

              {wordCount > 0 && outputWordCount > 0 && (
                <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
                  <p className="text-sm text-muted-foreground">
                    📊 Word count: {wordCount} → {outputWordCount} ({outputWordCount > wordCount ? '+' : ''}{outputWordCount - wordCount} words)
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <RefreshCw className="h-12 w-12 text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">Paraphrased text will appear here</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Choose a mode and enter your text</p>
            </div>
          )}
        </GlowCard>
      </div>
    </div>
  );
};

export default Paraphraser;
